import { defaultRuleRegistry } from '../rules/registry';
import type { Diagnostic, RuleEvaluationContext } from '../types';

import { normalizeFromRemoteControlUI } from './normalizers';
import { runRules } from './ruleRunner';

/**
 * Runs the rule registry against a routine. Only error-severity diagnostics
 * decide `ok` and block play; warning-severity diagnostics never block but
 * are returned in `warnings` so callers can surface them. Pure pipeline:
 * takes a fully-formed `RuleEvaluationContext`, does no synthesis. The
 * cascade-aware wrapper lives in `precheckForExecution`.
 *
 * Splitting this from `precheckForExecution` makes the cascade's recursion
 * termination structural rather than conventional: the cascade calls into
 * `runValidation` (no cascade branch), so it cannot re-enter itself.
 */
export function runValidation(
  routine: unknown,
  ctx: RuleEvaluationContext,
):
  | { ok: true; warnings: Diagnostic[] }
  | { ok: false; errors: Diagnostic[]; warnings: Diagnostic[] } {
  const routineView = normalizeFromRemoteControlUI(
    routine as Parameters<typeof normalizeFromRemoteControlUI>[0],
  );

  const diagnostics = runRules(defaultRuleRegistry, routineView, ctx);

  const errors = diagnostics.filter((d) => d.severity !== 'warning');
  const warnings = diagnostics.filter((d) => d.severity === 'warning');

  return errors.length
    ? { ok: false, errors, warnings }
    : { ok: true, warnings };
}
